import React from 'react';
import ImportDialog from '../common/import/ImportDialog';
import { lagImportAPI } from '../../services/api/import.api';

interface TalentImportRad {
  kategori: string;
  underkategori: string | null;
  detaljkategori: string | null;
  talent: string;
}

interface TalentImportResultat {
  nyeKategorier: number;
  nyeTalenter: number;
  hoppetOver: number;
}

const api = lagImportAPI<TalentImportRad, TalentImportResultat>('/talent/import');

const KOLONNER = [
  { felt: 'kategori', overskrift: 'Kategori' },
  { felt: 'underkategori', overskrift: 'Underkategori' },
  { felt: 'detaljkategori', overskrift: 'Detaljkategori' },
  { felt: 'talent', overskrift: 'Talent' },
];

// Serveren slår sammen kategorier uten hensyn til store/små bokstaver og beholder første visning.
const normaliser = (s: string) => s.normalize('NFC').toLocaleLowerCase('nb');

const stiprefikser = (rad: TalentImportRad): string[] => {
  const sti = [rad.kategori, rad.underkategori, rad.detaljkategori].filter((l): l is string => !!l);
  return sti.map((_, i) => sti.slice(0, i + 1).join(' › '));
};

/** Antall ulike stiprefikser i de valgte radene som serveren melder som nye kategorier. */
const tellNyeKategorier = (nyeKategorier: unknown, valgte: TalentImportRad[]): number => {
  if (!Array.isArray(nyeKategorier)) return 0;
  const nye = new Set(nyeKategorier.map((k) => normaliser(String(k))));
  const brukt = new Set(valgte.flatMap(stiprefikser).map(normaliser));
  return Array.from(brukt).filter((sti) => nye.has(sti)).length;
};

const HJELPETEKST =
  'Last opp .xlsx eller .csv (UTF-8) med kolonnene Kategori | Underkategori | Detaljkategori | Talent. I Google Sheets: Fil → Last ned → Microsoft Excel (.xlsx).';

interface Props {
  open: boolean;
  onClose: () => void;
  onImportert: () => void;
}

const TalentImportDialog: React.FC<Props> = ({ open, onClose, onImportert }) => (
  <ImportDialog<TalentImportRad, TalentImportResultat>
    open={open}
    onClose={onClose}
    onImportert={onImportert}
    tittel="Importer talenter"
    hjelpetekst={HJELPETEKST}
    api={api}
    kolonner={KOLONNER}
    malFilnavn="talentimport.xlsx"
    oppsummering={(f, valgte) => {
      const nyeKategorier = tellNyeKategorier(f.nyeKategorier, valgte);
      const nye = valgte.filter((r) => r.status === 'ny').length;
      const finnes = valgte.filter((r) => r.status === 'finnes').length;
      return `${nyeKategorier} nye kategorier, ${nye} nye talenter, ${finnes} finnes allerede`;
    }}
    formaterResultat={(r) =>
      `Importerte ${r.nyeKategorier} kategorier og ${r.nyeTalenter} talenter (${r.hoppetOver} fantes fra før)`
    }
  />
);

export default TalentImportDialog;
